import { Button, FormControl, FormLabel, Input, Modal, ModalClose, ModalDialog, ModalOverflow, Stack, Typography, Select, Option, Textarea, Chip, Box, Divider, CircularProgress, Alert, Switch, Tooltip, IconButton, Card, Sheet } from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutContext } from "../../context";
import { APICreateProfile, APIUpdateProfile, APIGetProfileDetail, APIGetConnectTokens, APICreateConnectToken, TELEGRAM_BOT_LINK, TELEGRAM_PATIENT_BOT_LINK, APIToggleBlockProfile, APIGetSpecializations, APIGetPatientDoctorRelations, APIGetDoctors, APICreatePatientDoctorRelation, APIDeletePatientDoctorRelation, APIRegenerateProfileToken, APIDeactivateConnectToken } from "../../api";
import { LinkRounded, ContentCopyRounded, RefreshRounded, CheckRounded, BlockRounded, ChatRounded, LockOpenRounded, LinkOffRounded, AddRounded } from '@mui/icons-material';
import ChatBubble from "../MessagesChat/ChatBubble";
import AvatarWithStatus from "../MessagesChat/AvatarWithStatus";
import { useNavigate } from "react-router";
import ModalBlockProfile from "./ModalBlockProfile";
import DoctorCalendarField from "../calendar/DoctorCalendarField";


export const USER_TYPES = [
    { value: 'patient', label: 'Пациент' },
    { value: 'doctor', label: 'Врач' },
    { value: 'duty_doctor', label: 'Дежурный врач' },
    { value: 'admin', label: 'Администратор' },
    { value: 'supervisor', label: 'Руководитель' },
    { value: 'moderator', label: 'Модератор' },
];

export default function ModalUser() {
    const { token, loadPatients, account, loadDoctors, modalUser, setModalUser, currentDoctor } = useContext(LayoutContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [specializations, setSpecializations] = useState([]);
    const [specsLoading, setSpecsLoading] = useState(false);

    const [connectTokens, setConnectTokens] = useState({});
    const [generatingToken, setGeneratingToken] = useState(false);
    const [copiedToken, setCopiedToken] = useState(null);

    const [relations, setRelations] = useState([]);
    const [relationsLoading, setRelationsLoading] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [newDoctorId, setNewDoctorId] = useState(null);
    const [addingRelation, setAddingRelation] = useState(false);
    const [requests, setRequests] = useState([]);
    const [requestsLoading, setRequestsLoading] = useState(false);

    const isAdmin = (currentDoctor?.type_profile.includes('admin') || currentDoctor?.type_profile?.includes('admin')) || (currentDoctor?.type_profile.includes('supervisor') || currentDoctor?.type_profile?.includes('supervisor'));

    const isAdminOrModerator = isAdmin || (currentDoctor?.type_profile.includes('moderator') || currentDoctor?.type_profile?.includes('moderator'));

    // Состояние формы
    const [formData, setFormData] = useState({
        name: '',
        type_profile: [],
        phone: '',
        email: '',
        specialization: [],
        is_active: true,
        is_block: false,
        user_id: '',
        user_data: {
            full_name: '',
            username: ''
        },
        recent_messages: [],
        chat: null,
        // === Новые поля ===
        connection_link: null,   // deeplink ссылка врача (для роли doctor)
        profile_token: null,
        block_reason: null,
        blocked_by: null,
        blocked_at: null,
        response_time_hours: '',
        tariff_end_date: '',
    });

    // Окно блокировки с причиной
    const [blockModalOpen, setBlockModalOpen] = useState(false);
    // Локальное состояние «скопировано» для DoctorConnectionLinkSection
    const [doctorLinkCopied, setDoctorLinkCopied] = useState(false);
    const [regeneratingProfileToken, setRegeneratingProfileToken] = useState(false);

    // Загрузка данных пользователя при открытии модального окна для редактирования
    useEffect(() => {
        if (modalUser.open) {
            loadSpecializations();
            if (modalUser.userId) {
                loadUserData();
                loadConnectTokens();
            } else {
                resetForm();
            }
        }
    }, [modalUser.open, modalUser.userId]);

    // Отдельный эффект для загрузки связок (только когда профиль пациента открыт)
    useEffect(() => {
        if (modalUser.open && modalUser.userId && isAdminOrModerator) {
            loadRelations();
            loadDoctorsForRelations();
            loadRequests();
        }
    }, [modalUser.open, modalUser.userId]);

    useEffect(() => {
        // Если у пользователя нет ролей, требующих specialization, сбрасываем specialization
        const requiresSpecialization = formData.type_profile.some(type => 
            ['admin', 'supervisor', 'psychologist', 'doctor'].includes(type)
        );
        
        if (!requiresSpecialization && formData.specialization) {
            setFormData(prev => ({
                ...prev,
                specialization: [],
            }));
        }
    }, [formData.type_profile]);

    const loadUserData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await APIGetProfileDetail(modalUser.userId, token);
            if (response.success) {
                const user = response.profile;
                setFormData({
                    name: user.name || '',
                    type_profile: Array.isArray(user.type_profile) ? user.type_profile : [user.type_profile].filter(Boolean),
                    phone: user.phone || '',
                    email: user.email || '',
                    specialization: Array.isArray(user.specialization)
                        ? user.specialization.map(s => s.id ?? s)
                        : [],
                    is_active: user.is_active !== undefined ? user.is_active : true,
                    is_block: user.is_block || false,
                    user_id: user.user?.id || '',
                    user_data: {
                        full_name: user.user?.full_name || '',
                        username: user.user?.username || ''
                    },
                    recent_messages: user.recent_messages || [],
                    chat: user.chat || null,
                    // === Новые поля ===
                    connection_link: user.connection_link || null,
                    profile_token: user.profile_token || null,
                    block_reason: user.block_reason || null,
                    blocked_by: user.blocked_by || null,
                    blocked_at: user.blocked_at || null,
                    response_time_hours: user.response_time_hours ?? '',
                    tariff_end_date: user.tariff_end_date || '',
                });
            }
        } catch (error) {
            console.error("Ошибка при загрузке данных пользователя:", error);
            setError("Ошибка при загрузке данных пользователя");
        } finally {
            setLoading(false);
        }
    };

    const loadRelations = async () => {
        if (!modalUser.userId) return;
        setRelationsLoading(true);
        try {
            const response = await APIGetPatientDoctorRelations(token, modalUser.userId);
            if (response.success) setRelations(response.relations || []);
        } catch (e) {
            console.error('Ошибка загрузки связок', e);
        } finally {
            setRelationsLoading(false);
        }
    };

    const loadDoctorsForRelations = async () => {
        try {
            const response = await APIGetDoctors(token);
            setDoctors(response.doctors || []);
        } catch (e) {
            console.error('Ошибка загрузки врачей', e);
        }
    };

    const handleAddRelation = async () => {
        if (!newDoctorId || !modalUser.userId) return;
        setAddingRelation(true);
        try {
            const response = await APICreatePatientDoctorRelation(token, modalUser.userId, newDoctorId);
            if (response.success) {
                setNewDoctorId(null);
                await loadRelations();
            }
        } catch (e) {
            setError('Ошибка при создании связки');
        } finally {
            setAddingRelation(false);
        }
    };

    const handleDeleteRelation = async (relationId) => {
        const rel = relations.find(r => r.id === relationId);
        const doctorName = rel?.doctor?.name || 'этого врача';
        
        const confirmed = window.confirm(
            `Вы уверены, что хотите отвязать врача "${doctorName}"?\n\n` +
            `Пациент потеряет доступ к чату с этим врачом.`
        );
        if (!confirmed) return;

        try {
            await APIDeletePatientDoctorRelation(token, modalUser.userId, relationId);
            await loadRelations();
            setSuccess(`Врач "${doctorName}" успешно откреплён`);
        } catch (e) {
            setError('Ошибка при удалении связки');
        }
    };

    const loadSpecializations = async () => {
        setSpecsLoading(true);
        try {
            const response = await APIGetSpecializations(token);
            if (response.success) {
                setSpecializations(response.specializations || []);
            }
        } catch (e) {
            console.error('Ошибка загрузки специализаций', e);
        } finally {
            setSpecsLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            type_profile: ['patient'],
            phone: '',
            email: '',
            specialization: [],
            is_active: true,
            is_block: false,
            user_id: '',
            user_data: {
                full_name: '',
                username: ''
            },
            recent_messages: [],
            response_time_hours: '',
        });
        setError(null);
        setSuccess(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const loadRequests = async () => {
        if (!modalUser.userId) return;
        setRequestsLoading(true);
        try {
            const r = await APIGetConnectionRequests(token, modalUser.userId);
            if (r.success) setRequests(r.requests || []);
        } catch (e) { /* тихо */ } finally { setRequestsLoading(false); }
    };

    const handleToggleRelation = async (rel) => {
        try {
            if (rel.is_active) {
                await APIDeletePatientDoctorRelation(token, modalUser.userId, rel.id); // приостановить (чат остаётся)
            } else {
                await APICreatePatientDoctorRelation(token, modalUser.userId, rel.doctor.id); // возобновить
            }
            loadRelations();
        } catch (e) { setError('Не удалось изменить связку'); }
    };

    const handleApproveRequest = async (id) => {
        try { await APIApproveConnectionRequest(token, id); await loadRelations(); await loadRequests(); }
        catch (e) { setError('Не удалось одобрить запрос'); }
    };
    const handleRejectRequest = async (id) => {
        try { await APIRejectConnectionRequest(token, id); await loadRequests(); }
        catch (e) { setError('Не удалось отклонить запрос'); }
    };

    // Компонент для блока пригласительной ссылки
    const ConnectTokenSection = () => {
        const tokenData = connectTokens[modalUser.userId];
        
        const generateConnectToken = async () => {
            if (!modalUser.userId) return;
            
            try {
            setGeneratingToken(true);
            const response = await APICreateConnectToken(token, {
                target_profile_id: modalUser.userId
            });
            
            await loadConnectTokens();
            setSuccess("Ссылка успешно сгенерирована");
            } catch (error) {
            console.error("Ошибка при генерации токена:", error);
            setError("Ошибка при генерации ссылки");
            } finally {
            setGeneratingToken(false);
            }
        };

        const regenerateConnectToken = async (currentTokenId) => {
            try {
            setGeneratingToken(true);
            
            await APIDeactivateConnectToken(token, currentTokenId);
            await APICreateConnectToken(token, {
                target_profile_id: modalUser.userId
            });
            
            await loadConnectTokens();
            setSuccess("Ссылка успешно пересоздана");
            } catch (error) {
            console.error("Ошибка при регенерации токена:", error);
            setError("Ошибка при перегенерации ссылки");
            } finally {
            setGeneratingToken(false);
            }
        };

        const copyToClipboard = (tokenStr) => {
            const link = `${TELEGRAM_BOT_LINK}?start=${tokenStr}`;
            navigator.clipboard.writeText(link);
            setCopiedToken(tokenStr);
            setTimeout(() => setCopiedToken(null), 2000);
            setSuccess("Ссылка скопирована в буфер обмена");
        };

        if (!modalUser.userId) {
            return (
            <Alert color="neutral">
                Ссылка будет доступна после сохранения пользователя
            </Alert>
            );
        }

        if (!tokenData) {
            return (
            <Button
                variant="outlined"
                color="neutral"
                size="sm"
                loading={generatingToken}
                startDecorator={<LinkRounded />}
                onClick={generateConnectToken}
                fullWidth
            >
                Сгенерировать ссылку для подключения
            </Button>
            );
        }

        if (tokenData.connected_user) {
            return (
            <Alert color="success">
                Пользователь подключен: <b>{tokenData.connected_user.full_name || tokenData.connected_user.username}</b>
            </Alert>
            );
        }

        if (tokenData.is_active) {
            const fullLink = `${TELEGRAM_BOT_LINK}?start=${tokenData.token}`;
            
            return (
            <Stack spacing={1}>
                <Input 
                value={fullLink}
                size="sm"
                readOnly
                startDecorator={<LinkRounded />}
                endDecorator={
                    <Tooltip sx={{ zIndex: 10023 }} title={copiedToken === tokenData.token ? "Скопировано!" : "Копировать"}>
                        <IconButton
                            variant="plain" 
                            onClick={() => copyToClipboard(tokenData.token)}
                        >
                            {copiedToken === tokenData.token ? <CheckRounded /> : <ContentCopyRounded />}
                        </IconButton>
                    </Tooltip>
                }
                sx={{ width: '100%' }}
                />
                <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    loading={generatingToken}
                    onClick={() => regenerateConnectToken(tokenData.id)}
                    startDecorator={<RefreshRounded />}
                    sx={{ flex: 1 }}
                >
                    Пересоздать
                </Button>
                </Stack>
            </Stack>
            );
        }

        return (
            <Button
                variant="outlined"
                color="neutral"
                size="sm"
                loading={generatingToken}
                startDecorator={<LinkRounded />}
                onClick={generateConnectToken}
                fullWidth
            >
                Сгенерировать ссылку
            </Button>
        );
    };

    // === Секция персональной deeplink-ссылки врача (profile_token) ===
    // Это ОТЛИЧАЕТСЯ от ConnectTokenSection (которая работает с ConnectToken).
    // ConnectToken — это токены подключения для конкретного пациента.
    // profile_token / connection_link — это персональная ссылка врача,
    // по которой пациент подключается именно к этому врачу.
    const DoctorConnectionLinkSection = () => {
        if (!modalUser.userId) return null;
        if (!formData.type_profile?.includes('doctor')) return null;

        const link = formData.connection_link;

        const copy = () => {
            if (!link) return;
            navigator.clipboard.writeText(link);
            setDoctorLinkCopied(true);
            setSuccess("Ссылка для пациентов скопирована");
            setTimeout(() => setDoctorLinkCopied(false), 2000);
        };

        const regenerate = async () => {
            try {
                setRegeneratingProfileToken(true);
                const response = await APIRegenerateProfileToken(modalUser.userId, token);
                if (response?.success) {
                    setFormData(prev => ({
                        ...prev,
                        profile_token: response.profile_token,
                        connection_link: response.connection_link
                    }));
                    setSuccess("Ссылка пересоздана — старая больше не работает");
                }
            } catch (e) {
                setError(e?.response?.data?.error || "Не удалось пересоздать ссылку");
            } finally {
                setRegeneratingProfileToken(false);
            }
        };

        if (!link) {
            return (
                <Alert color="warning">
                    Ссылка для пациентов появится после сохранения профиля врача
                </Alert>
            );
        }

        return (
            <Stack spacing={1}>
                <Typography level="body-xs" color="neutral">
                    Персональная ссылка врача — пациент по ней подключится именно к этому врачу
                </Typography>
                <Input
                    value={link}
                    size="sm"
                    readOnly
                    startDecorator={
                        <Chip size="sm" variant="soft" color="primary">Для пациента</Chip>
                    }
                    endDecorator={
                        <Tooltip sx={{ zIndex: 10023 }} title={doctorLinkCopied ? "Скопировано!" : "Копировать"}>
                            <IconButton variant="plain" onClick={copy}>
                                {doctorLinkCopied ? <CheckRounded /> : <ContentCopyRounded />}
                            </IconButton>
                        </Tooltip>
                    }
                    sx={{ width: '100%' }}
                />
                <Button
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    startDecorator={<RefreshRounded />}
                    onClick={regenerate}
                    loading={regeneratingProfileToken}
                >
                    Пересоздать ссылку
                </Button>
            </Stack>
        );
    };

    // Функция загрузки токенов
    const loadConnectTokens = async () => {
        try {
            const response = await APIGetConnectTokens(token);
            const tokensMap = {};
            
            (response.tokens || []).forEach(token => {
            if (token.target_profile) {
                tokensMap[token.target_profile.id] = token;
            }
            });
            
            setConnectTokens(tokensMap);
        } catch (error) {
            console.error("Ошибка при загрузке токенов:", error);
        }
    };


    const handleTypeProfileChange = (event, newValue) => {
        // Если пытаются выбрать "patient" вместе с другими типами
        if (newValue.includes('patient') && newValue.length > 1) {
            // Оставляем только "patient", остальные убираем
            setFormData(prev => ({
                ...prev,
                type_profile: ['patient']
            }));
        } 
        // Если пытаются выбрать другие типы (кроме patient) и среди них есть "patient"
        else if (!newValue.includes('patient') && formData.type_profile.includes('patient')) {
            // Просто устанавливаем новые значения (patient автоматически уберется)
            setFormData(prev => ({
                ...prev,
                type_profile: newValue
            }));
        }
        // Если выбрали несколько типов без patient - нормально
        else if (!newValue.includes('patient')) {
            setFormData(prev => ({
                ...prev,
                type_profile: newValue
            }));
        }
        // Если выбрали только patient или пустой массив
        else {
            setFormData(prev => ({
                ...prev,
                type_profile: newValue
            }));
        }
    };

    const handleSwitchChange = (name) => (event) => {
        setFormData(prev => ({
            ...prev,
            [name]: event.target.checked
        }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            // Валидация обязательных полей
            if (!formData?.name?.trim()) {
                throw new Error("Имя пользователя обязательно для заполнения");
            }

            // Подготовка данных для отправки
            const submitData = {
                name: formData?.name?.trim(),
                type_profile: formData?.type_profile,
                phone: formData?.phone?.trim(),
                email: formData?.email?.trim() || null,
                specialization_ids: formData.type_profile.includes('doctor') ? formData.specialization : [],
                is_active: formData.is_active,
                is_block: formData.is_block,
                response_time_hours: formData.type_profile.includes('doctor') && formData.response_time_hours !== ''
                    ? Number(formData.response_time_hours)
                    : null,
                tariff_end_date: formData.type_profile.includes('patient')
                    ? (formData.tariff_end_date || null)
                    : null,
            };

            // Добавляем данные пользователя Telegram, если они заполнены
            if (formData.user_id) {
                submitData.user_id = formData.user_id;
            }

            if (formData.user_data.full_name || formData.user_data.username) {
                submitData.user_data = {
                    full_name: formData?.user_data?.full_name?.trim(),
                    username: formData?.user_data?.username?.trim()
                };
            }

            let response;
            if (modalUser.userId) {
                // Редактирование существующего пользователя
                response = await APIUpdateProfile(modalUser.userId, submitData, token);
            } else {
                // Создание нового пользователя
                response = await APICreateProfile(submitData, token);
            }

            if (response.success) {
                setSuccess(modalUser.userId ? 
                    "Данные пользователя успешно обновлены" : 
                    "Пользователь успешно создан"
                );

                // Обновляем списки данных
                await Promise.all([
                    loadPatients(),
                    loadDoctors && loadDoctors()
                ]);

                // Закрываем модальное окно через 1.5 секунды
                setTimeout(() => {
                    setModalUser({ open: false, userId: null, onLoadData: null });
                }, 1000);
            }
            if (modalUser.onLoadData) modalUser.onLoadData();
        } catch (error) {
            console.error("Ошибка при сохранении пользователя:", error);
            setError(error.message || "Ошибка при сохранении пользователя");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            {modalUser.open && (
                <Modal
                    sx={{ zIndex: 10021 }}
                    open={modalUser.open}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '600px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '600px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '550px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <ModalClose onClick={() => {
                                setModalUser({ open: false, userId: null, onLoadData: null });
                            }}/>
                            
                            <Typography sx={{ textAlign: 'center', mb: 2 }} level="h3">
                                {modalUser.userId ? 'Редактирование пользователя' : 'Создание пользователя'}
                            </Typography>

                            {error && (
                                <Alert color="danger" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert color="success" sx={{ mb: 2 }}>
                                    {success}
                                </Alert>
                            )}

                            {loading ? (
                                <Stack alignItems="center" justifyContent="center" p={4}>
                                    <CircularProgress />
                                    <Typography sx={{ mt: 2 }}>Загрузка данных...</Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={3}>
                                    <DoctorCalendarField
                                        key={`${modalUser.userId || 'new'}-${modalUser.open}`}
                                        profileId={modalUser.userId}
                                        isDoctor={formData.type_profile.some((role) => ['doctor', 'duty_doctor'].includes(role))}
                                    />
                                    {/* Основная информация */}
                                    <Box>
                                        <Typography level="h4" sx={{ mb: 2 }}>Основная информация</Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        
                                        <Stack spacing={2}>
                                            <FormControl required>
                                                <FormLabel>Имя пользователя</FormLabel>
                                                <Input 
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="Введите имя пользователя"
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel>Телефон</FormLabel>
                                                <Input 
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="+7 (XXX) XXX-XX-XX"
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel>Email</FormLabel>
                                                <Input 
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="you@mail.ru"
                                                />
                                            </FormControl>

                                            {isAdmin && (
                                                <FormControl>
                                                    <FormLabel>Типы профиля</FormLabel>
                                                    <Select
                                                        multiple
                                                        slotProps={{ listbox: { sx: { zIndex: 10022 } } }}
                                                        value={formData.type_profile}
                                                        onChange={handleTypeProfileChange}
                                                        renderValue={(selected) => (
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                {selected.map((item) => {
                                                                    return (
                                                                        <Chip key={item.value} variant="soft" color="primary" size="md">
                                                                            {USER_TYPES.find(type => type.value == item.value)?.label}
                                                                        </Chip>
                                                                    )
                                                                })}
                                                            </Box>
                                                        )}
                                                    >
                                                        {USER_TYPES.map((type) => (
                                                            <Option key={type.value} value={type.value}>
                                                                {type.label}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}

                                            {formData.type_profile.includes('doctor') && (
                                                <FormControl>
                                                    <FormLabel>Специализация</FormLabel>
                                                    {specsLoading ? (
                                                        <CircularProgress size="sm" />
                                                    ) : (
                                                        <Select
                                                            multiple
                                                            slotProps={{ listbox: { sx: { zIndex: 10022 } } }}
                                                            value={formData.specialization}
                                                            onChange={(_, newValue) => {
                                                                setFormData(prev => ({ ...prev, specialization: newValue }));
                                                            }}
                                                            renderValue={(selected) => (
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                                    {selected.map((item) => {
                                                                        const spec = specializations.find(s => s.id === item.value);
                                                                        return spec ? (
                                                                            <Chip key={item.value} variant="soft" color="primary" size="md">
                                                                                {spec.name}
                                                                            </Chip>
                                                                        ) : null;
                                                                    })}
                                                                </Box>
                                                            )}
                                                            placeholder="Выберите специализации..."
                                                        >
                                                            {specializations.map((spec) => (
                                                                <Option key={spec.id} value={spec.id}>
                                                                    {spec.name}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                </FormControl>
                                            )}

                                            {formData.type_profile.includes('doctor') && (
                                                <FormControl>
                                                    <FormLabel>Обычное время ответа (часы)</FormLabel>
                                                    <Input
                                                        type="number"
                                                        slotProps={{ input: { min: 1, max: 720, step: 1 } }}
                                                        value={formData.response_time_hours}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            response_time_hours: e.target.value
                                                        }))}
                                                        placeholder="Например, 24"
                                                    />
                                                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                                        Отображается пациенту в шапке чата как «обычно отвечает в течение N часов». Оставьте пустым, чтобы не показывать.
                                                    </Typography>
                                                </FormControl>
                                            )}
                                        </Stack>
                                    </Box>
                                    
                                    {formData.recent_messages && formData.recent_messages.length > 0 && (
                                        <Box>
                                            <Typography level="h4" sx={{ mb: 2 }}>Недавние сообщения</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Sheet color="neutral" variant="soft" sx={{ p: 2, borderRadius: 'lg' }}>
                                                <Stack direction={"column"} spacing={2}>
                                                    {formData?.recent_messages?.reverse()?.map((msg, index) => (
                                                        <Stack
                                                            key={msg?.id}
                                                            direction="row"
                                                            spacing={2}
                                                            sx={{ 
                                                                flexDirection: account?.id == msg?.sender?.id ? 'row-reverse' : 'row',
                                                                justifyContent: msg?.type_sender == "system" ? "center" : "normal"
                                                            }}
                                                        >
                                                            {account?.id != msg?.sender?.id && msg?.type_sender != "system" && (
                                                                <AvatarWithStatus
                                                                // online={message.sender?.online}
                                                                // src={message.sender?.avatar}
                                                                />
                                                            )}
                                                            <ChatBubble message={msg} isYou={account?.id == msg?.sender?.id} />
                                                        </Stack>


                                                        // <Card key={index} variant="outlined" size="sm">
                                                        //     <Stack direction="row" spacing={1} alignItems="center">
                                                        //         <Typography level="body2" color="neutral" sx={{ minWidth: 80 }}>
                                                        //             {new Date(msg.date_create).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        //         </Typography>
                                                        //         <Typography level="body2" sx={{ flex: 1 }}>
                                                        //             {msg.content || (msg.has_media ? `[Медиа: ${msg.media_type}]` : '[Сообщение]')}
                                                        //         </Typography>
                                                        //         {msg.is_dangerous && (
                                                        //             <Chip size="sm" color="danger" variant="soft">
                                                        //                 Опасно
                                                        //             </Chip>
                                                        //         )}
                                                        //     </Stack>
                                                        // </Card>
                                                    ))}
                                                </Stack>
                                            </Sheet>
                                            
                                            <Button
                                                onClick={() => {
                                                    setModalUser({ open: false, userId: null, onLoadData: null });
                                                    navigate(`/chats?chatId=${formData?.chat?.id}`);
                                                }}
                                                startDecorator={<ChatRounded />}
                                                size="sm"
                                                variant="soft"
                                                sx={{ mt: 2 }}
                                            >
                                                Перейти в чат
                                            </Button>
                                        </Box>
                                    )}

                                    {formData.type_profile.includes('patient') && (() => {
                                        const daysLeft = (() => {
                                            if (!formData.tariff_end_date) return 0;
                                            const end = new Date(formData.tariff_end_date + 'T00:00:00');
                                            const today = new Date(); today.setHours(0, 0, 0, 0);
                                            const diff = Math.round((end - today) / 86400000);
                                            return diff >= 0 ? diff + 1 : 0;
                                        })();
                                        return (
                                            <FormControl>
                                                <FormLabel>Дата окончания тарифа</FormLabel>
                                                <Input
                                                    type="date"
                                                    name="tariff_end_date"
                                                    value={formData.tariff_end_date || ''}
                                                    onChange={handleChange}
                                                />
                                                <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                                    Дата — последний день доступа к переписке включительно.
                                                </Typography>
                                                {formData.tariff_end_date && (
                                                    <Chip size="sm" variant="soft" sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                                                        color={daysLeft > 0 ? 'success' : 'danger'}>
                                                        {daysLeft > 0 ? `Тариф активен, осталось дней: ${daysLeft}` : 'Тариф истёк'}
                                                    </Chip>
                                                )}
                                            </FormControl>
                                        );
                                    })()}

                                    {isAdminOrModerator && formData.type_profile.includes('patient') && modalUser.userId && (
                                        <Box>
                                            <Typography level="h4" sx={{ mb: 2 }}>Связки с врачами</Typography>
                                            <Divider sx={{ mb: 2 }} />

                                            {relationsLoading ? (
                                                <CircularProgress size="sm" />
                                            ) : (
                                                <Stack spacing={1}>
                                                    {relations.length === 0 && (
                                                        <Typography level="body2" color="neutral">Связок пока нет</Typography>
                                                    )}
                                                    {relations.map(rel => (
                                                        <Stack key={rel.id} direction="row" justifyContent="space-between"
                                                            alignItems="center" sx={{
                                                                py: 1, px: 1.5, borderRadius: 20,
                                                                border: '1px solid', borderColor: 'divider'
                                                            }}>
                                                            <Box>
                                                                <Typography level="title-md" fontWeight="md">
                                                                    {rel.doctor.name}
                                                                </Typography>
                                                                {rel.doctor.specialization?.length > 0 && (
                                                                    <Chip variant="soft" color="primary">
                                                                        {rel.doctor.specialization.map(s => s.name).join(', ')}
                                                                    </Chip>
                                                                )}
                                                            </Box>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Chip size="sm" variant="soft" color={rel.is_active ? 'success' : 'neutral'}>
                                                                    {rel.is_active ? 'Активна' : 'Приостановлена'}
                                                                </Chip>
                                                                <Tooltip title={rel.is_active ? 'Приостановить связку' : 'Возобновить связку'}>
                                                                    <IconButton size="sm" variant="soft" color={rel.is_active ? 'warning' : 'success'}
                                                                        onClick={() => handleToggleRelation(rel)}>
                                                                        {rel.is_active ? <LinkOffRounded /> : <LinkRounded />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        </Stack>
                                                    ))}

                                                    {/* Добавление новой связки */}
                                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                        <Select
                                                            placeholder="Выбрать врача..."
                                                            value={newDoctorId}
                                                            onChange={(_, v) => setNewDoctorId(v)}
                                                            sx={{ flex: 1 }}
                                                            slotProps={{ listbox: { sx: { zIndex: 10022, maxHeight: 300, overflow: 'auto' } } }}
                                                        >
                                                            {doctors
                                                                .filter(d => !relations.some(r => r.doctor.id === d.id))
                                                                .map(d => (
                                                                    <Option key={d.id} value={d.id}>
                                                                        {d.name}
                                                                        {d.specialization?.length > 0 && (<Typography component={'span'} level="body2" color="neutral">({d.specialization.map(s => s.name).join(', ')})</Typography>)}
                                                                    </Option>
                                                                ))
                                                            }
                                                        </Select>
                                                        <Button
                                                            variant="solid" color="primary"
                                                            loading={addingRelation}
                                                            disabled={!newDoctorId}
                                                            startDecorator={<AddRounded />}
                                                            onClick={handleAddRelation}
                                                        >
                                                            Добавить
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            )}
                                        </Box>
                                    )}

                                    {isAdminOrModerator && formData.type_profile.includes('patient') && modalUser.userId && (
                                        <Box>
                                            <Typography level="h4" sx={{ mb: 2 }}>Запросы на подключение</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            {requestsLoading ? (
                                                <CircularProgress size="sm" />
                                            ) : requests.length === 0 ? (
                                                <Typography level="body2" color="neutral">Запросов нет</Typography>
                                            ) : (
                                                <Stack spacing={1}>
                                                    {requests.map(req => (
                                                        <Stack key={req.id} direction="row" justifyContent="space-between" alignItems="center"
                                                            sx={{ py: 1, px: 1.5, borderRadius: 12, border: '1px solid', borderColor: 'divider' }}>
                                                            <Box>
                                                                <Typography level="title-md" fontWeight="md">{req.doctor.name}</Typography>
                                                                {req.doctor.specialization?.length > 0 && (
                                                                    <Typography level="body-xs" color="neutral">
                                                                        {req.doctor.specialization.map(s => s.name).join(', ')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            {req.status === 'pending' ? (
                                                                <Stack direction="row" spacing={1}>
                                                                    <Button size="sm" variant="solid" color="success"
                                                                        onClick={() => handleApproveRequest(req.id)}>Одобрить</Button>
                                                                    <Button size="sm" variant="soft" color="danger"
                                                                        onClick={() => handleRejectRequest(req.id)}>Отклонить</Button>
                                                                </Stack>
                                                            ) : (
                                                                <Chip size="sm" variant="soft"
                                                                    color={req.status === 'approved' ? 'success' : 'danger'}>
                                                                    {req.status === 'approved' ? 'Одобрена' : 'Отклонена'}
                                                                </Chip>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Box>
                                    )}

                                    {/* Настройки */}
                                    <Box>
                                        <Box>
                                            <Typography level="h4" sx={{ mb: 2 }}>Ссылка для подключения</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <ConnectTokenSection />
                                        </Box>

                                        {/* Персональная ссылка врача (если в профиле есть тип 'doctor') */}
                                        {formData.type_profile?.includes('doctor') && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography level="h4" sx={{ mb: 2 }}>Ссылка пациента к этому врачу</Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <DoctorConnectionLinkSection />
                                            </Box>
                                        )}

                                        {/* Информация о блокировке, если профиль заблокирован */}
                                        {formData.is_block && (
                                            <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
                                                <Stack spacing={0.5} sx={{ width: '100%' }}>
                                                    <Typography level="title-sm" color="danger">
                                                        Профиль заблокирован
                                                    </Typography>
                                                    {formData.block_reason && (
                                                        <Typography level="body-sm">
                                                            <b>Причина:</b> {formData.block_reason}
                                                        </Typography>
                                                    )}
                                                    {formData.blocked_by?.name && (
                                                        <Typography level="body-xs" color="neutral">
                                                            Заблокировал: {formData.blocked_by.name}
                                                            {formData.blocked_at && ` • ${new Date(formData.blocked_at).toLocaleString('ru-RU')}`}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Alert>
                                        )}

                                        <Typography level="h4" sx={{ my: 2 }}>Настройки</Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        
                                        <Stack spacing={2}>
                                            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                                                <FormLabel>Активный профиль</FormLabel>
                                                <Switch
                                                    checked={formData.is_active}
                                                    onChange={handleSwitchChange('is_active')}
                                                    color={formData.is_active ? 'success' : 'neutral'}
                                                />
                                            </FormControl>

                                            {isAdmin && (
                                                <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                                                    <FormLabel>Блокировка профиля</FormLabel>
                                                    <Switch
                                                        checked={formData.is_block}
                                                        onChange={handleSwitchChange('is_block')}
                                                        color={formData.is_block ? 'danger' : 'neutral'}
                                                    />
                                                </FormControl>
                                            )}
                                        </Stack>
                                    </Box>
                                    
                                    <Stack direction={"column"} spacing={2}>
                                        {/* Кнопка для быстрой блокировки/разблокировки */}
                                        <Button
                                            variant="soft"
                                            color={formData.is_block ? "success" : "danger"}
                                            startDecorator={formData.is_block ? <LockOpenRounded /> : <BlockRounded />}
                                            onClick={async () => {
                                                // При разблокировке — toggle напрямую (без причины)
                                                if (formData.is_block) {
                                                    try {
                                                        setSaving(true);
                                                        const response = await APIToggleBlockProfile(
                                                            token,
                                                            modalUser.userId,
                                                            formData.type_profile[0] || 'patient',
                                                            ''
                                                        );
                                                        if (response.success) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                is_block: response.is_block,
                                                                block_reason: null,
                                                                blocked_by: null,
                                                                blocked_at: null,
                                                            }));
                                                            setSuccess(response.message);
                                                            if (modalUser.onLoadData) modalUser.onLoadData();
                                                        }
                                                    } catch (error) {
                                                        setError(error?.response?.data?.error || "Ошибка при разблокировке");
                                                    } finally {
                                                        setSaving(false);
                                                    }
                                                }
                                                // При блокировке — открываем модалку с причиной
                                                else {
                                                    setBlockModalOpen(true);
                                                }
                                            }}
                                            loading={saving}
                                            disabled={!modalUser.userId}
                                        >
                                            {formData.is_block ? "Разблокировать профиль" : "Заблокировать профиль"}
                                        </Button>

                                        {/* Кнопки действий */}
                                        <Stack direction="row" spacing={2} sx={{ mt: 0 }}>
                                            <Button
                                                variant="outlined"
                                                color="neutral"
                                                fullWidth
                                                onClick={() => setModalUser({ open: false, userId: null, onLoadData: null })}
                                                disabled={saving}
                                            >
                                                Отмена
                                            </Button>
                                            <Button
                                                variant="solid"
                                                color="primary"
                                                fullWidth
                                                onClick={handleSubmit}
                                                loading={saving}
                                                disabled={!formData.name.trim()}
                                            >
                                                {modalUser.userId ? 'Сохранить' : 'Создать'}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            )}
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}

            {/* Окно блокировки с причиной (используется при первой блокировке) */}
            <ModalBlockProfile
                open={blockModalOpen}
                profile={{
                    id: modalUser.userId,
                    name: formData.name,
                    type_profile: formData.type_profile,
                }}
                onClose={() => setBlockModalOpen(false)}
                onBlocked={(response) => {
                    setFormData(prev => ({
                        ...prev,
                        is_block: response.is_block,
                        block_reason: response.block_reason,
                    }));
                    setSuccess(response.message || 'Профиль заблокирован');
                    if (modalUser.onLoadData) modalUser.onLoadData();
                    // Перезагружаем данные чтобы получить blocked_by/blocked_at
                    if (modalUser.userId) loadUserData();
                }}
            />
        </AnimatePresence>
    );
}